import _ from 'lodash';
import { generatePath, useHistory } from 'react-router';
import React from 'react';

// Material UI
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import ArchiveIcon from '@material-ui/icons/Archive';
import ViewIcon from '@material-ui/icons/Visibility';
import WarningIcon from '@material-ui/icons/Warning';

// Components
import { EnhancedTable, IEnhancedTableColumn, IEnhancedTableRow } from '../../_core/_ui/table.components';
import { FormInput } from '../../_core/_ui/forms.component';
import Tooltip from "@material-ui/core/Tooltip";
import { getAuthUser } from '../../../lib/services/auth.service';
import { toListSentence } from '../../../lib/helpers/conversion.helpers';
import { useCompanyHasReportCards, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { SportTerminology } from '../../../lib/types';
import { getPlayerStatus } from '../../../lib/utils/player.utils';
import { getPlayerName } from '../../../lib/utils/player-name.service';
import { hasAnyRoles } from '../../../lib/services/user.service';
import { ROLES, STAFF_PATHS } from '../../../../_configuration';
import { Box } from '@material-ui/core';
import { OnDemandReportCardActions } from '../../ReportCardAccess/on-demand-report-card-actions.component';
import { ReportCardActionsProvider } from '../../ReportCardAccess/report-card-actions.context';
import { ReportCardStatusChip } from '../../ReportCardAccess/report-card-status-chip.component';
import { ContactReachIcon } from '../../PlayerContact/contact-reach-icon.component';

interface IPlayerList {
  playerAssessments: Array<IPlayerAssessment>;
  search: Function;
  loading: boolean;
  fetchError: boolean;
  selected: IPlayerAssessment[];
  setSelected: Function;
  removePlayerAssessments: Function;
}

export const PlayerList: React.FC<IPlayerList> = ({
  playerAssessments,
  search,
  loading,
  fetchError,
  selected,
  setSelected,
  removePlayerAssessments
}) => {
  const termLookup = useCompanySportTerminology();
  const hasReportCards = useCompanyHasReportCards();
  const athletesTerm = pluralTerm(termLookup[SportTerminology.ATHLETE]);

  const user = getAuthUser();
  const history = useHistory();
  const rows: IEnhancedTableRow[] = [];

  const hasConfigRole = hasAnyRoles({ageGroupId: playerAssessments[0]?.ageGroup._id, roles: [ROLES.CONFIGURATION]});

  _.each(playerAssessments, (playerAssessment: IPlayerAssessment) => {
    rows.push({
      _id: playerAssessment._id,
      data: playerAssessment,
      cells: [
        <TableCell key="actions" padding="checkbox">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <IconButton onClick={() => history.push(generatePath(STAFF_PATHS.PLAYER_ASSESSMENT, { playerId: playerAssessment.player?._id, playerAssessmentId: playerAssessment._id }))}>
              {hasConfigRole ? <EditIcon /> : <ViewIcon />}
            </IconButton>
            {!!playerAssessment.unappliedActionsCount && (
              <Tooltip title={`Found ${playerAssessment.unappliedActionsCount} new scores`}>
                <WarningIcon color='error' />
              </Tooltip>
            )}
            <ContactReachIcon contactReach={playerAssessment.contactReach} />
          </Box>
        </TableCell>,
        <TableCell key="session">{playerAssessment.assessmentSessionCount}</TableCell>,
        <TableCell key="name">{getPlayerName(playerAssessment)}</TableCell>,
        <TableCell key="ageGroup">{playerAssessment.ageGroup?.name}</TableCell>,
        <TableCell key="position">{playerAssessment.position?.name}</TableCell>,
        <TableCell key="status">{getPlayerStatus(playerAssessment)}</TableCell>,
        hasReportCards && (
          <TableCell key="reportCard">
            <Box display="flex" alignItems="center">
              <ReportCardStatusChip access={playerAssessment.reportCardAccess} />
              <OnDemandReportCardActions playerAssessment={playerAssessment} />
            </Box>
          </TableCell>
        ),
      ].filter(Boolean),
      disableSelect: playerAssessment.editingDisabled
    });
  });

  const columns = [
    { key: 'actions', width: '112px', disableSort: true },
    { key: 'assessmentSessionCount', width: '92px', label: pluralTerm(termLookup[SportTerminology.SESSION]) },
    { key: 'name', label: 'Name', path: 'player.lastName' },
    { key: 'ageGroup', label: 'Age Group', path: 'ageGroup.name' },
    { key: 'position', label: 'Position', path: 'position.name' },
    { key: 'status', label: 'Status', disableSort: true },
    hasReportCards && { key: 'reportCard', label: 'Report Card', disableSort: true },
  ].filter(Boolean) as IEnhancedTableColumn[];

  return (
    <ReportCardActionsProvider>
      <EnhancedTable
        numCols={columns.length}
        rows={rows}
        columns={columns}
        defaultOrder="player.lastName"
        entityName="players"
        showError={fetchError}
        showLoading={loading && rows.length === 0}
        title={`${athletesTerm} in Assessment`}
        selected={selected}
        setSelected={setSelected}
        selectedAction={(
          <Tooltip title="Remove from Assessment">
            <IconButton aria-label="archive" onClick={() => {
                const playerNames = selected.map(getPlayerName);

              if (window.confirm(`Are you sure you want to remove ${toListSentence(playerNames)} from the assessment?`)) {
                removePlayerAssessments(selected.map(item => item._id));
                setSelected([]);
              }
            }}>
              <ArchiveIcon />
            </IconButton>
          </Tooltip>
        )}
        filterComponent={(
          <FormInput
            placeholder={`Search ${athletesTerm}..`}
            onKeyUp={(e: any) => search(e.currentTarget.value)} />
        )}
        noResultsMessage={user?.settings.activeAgeGroupIds.length ? 'No Results Found' : 'No Age Groups Selected'}
      />
    </ReportCardActionsProvider>
  );
};
